import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Crown, Shield, Star, Users, Gamepad2, Swords, History,
  DoorOpen, Send, Loader2, CheckCircle2,
} from 'lucide-react';
import { api, parseApiError, type ClanPublicPageResponse, type ClanScopedRole } from '../lib/api';
import { clanRoleDisplayName } from '../lib/roles';
import { CoverImage } from './CoverImage';
import { StatusBadge } from './StatusBadge';
import { StatCell, PLACE_MEDALS } from './OrgPublicPageView';

// ──────────────────────────────────────────────────────────────────────────────
// ClanPublicPageView — presentational render of the org-scoped public clan
// profile (GET /public/clan/:name).
//
// Pure function of `data` (ClanPublicPageResponse) — no fetching, no
// document-level side effects (no accent injection: the clan response has no
// accentColor field, unlike the org page). The one piece of local state is
// the Apply-to-join flow, lifted from ClanDetail.tsx's apply CTA/modal/
// mutation — that's page-owned interactivity, not a data fetch, so it stays
// here rather than in ClanHome.
//
// House style: iron/forge palette + font-display uppercase micro-labels +
// bordered cards (no shadows-as-glow, no gradients beyond the hero scrim
// needed for legibility) — matches ClanDetail/ClanBrandingHero, the other
// clan-facing surfaces, rather than the org page's older gray/accent look.
// ──────────────────────────────────────────────────────────────────────────────

function monogram(name: string | undefined | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function SectionLabel({ icon: Icon, children }: { icon: typeof Crown; children: React.ReactNode }) {
  return (
    <h2 className="mb-4 flex items-center gap-2 font-display text-xs font-black uppercase tracking-[0.22em] text-iron-500">
      <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
      {children}
    </h2>
  );
}

// Leadership rows come back with a friendly label already ('Owner' | 'Leader'
// | 'Captain') — see index.ts's /public/clan/:name handler.
function leadershipIcon(role: string) {
  if (role === 'Owner') return <Crown className="h-4 w-4 shrink-0 text-yellow-400" strokeWidth={2.5} />;
  if (role === 'Captain') return <Star className="h-4 w-4 shrink-0 text-forge-400" strokeWidth={2.5} />;
  return <Shield className="h-4 w-4 shrink-0 text-iron-400" strokeWidth={2.5} />;
}

// Full roster rows carry the raw ClanScopedRole ('CLAN_OWNER' | 'CLAN_LEADER'
// | 'CLAN_MEMBER') — same vocabulary lib/roles.ts's clanRoleDisplayName maps.
function memberIcon(role: string) {
  if (role === 'CLAN_OWNER') return <Crown className="h-4 w-4 shrink-0 text-yellow-400" strokeWidth={2.5} />;
  if (role === 'CLAN_LEADER') return <Shield className="h-4 w-4 shrink-0 text-iron-400" strokeWidth={2.5} />;
  return <Gamepad2 className="h-4 w-4 shrink-0 text-iron-500" strokeWidth={2.5} />;
}

export function ClanPublicPageView({ data }: { data: ClanPublicPageResponse }) {
  const { clan, leadership, members, teams, stats, comps, history } = data;

  const [applyOpen, setApplyOpen] = useState(false);
  const [gamertag, setGamertag] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Lifted from ClanDetail.tsx's applyMut (~L32-40) — same endpoint, same
  // gamertag+message shape. clan.name normalizes fine server-side even
  // though it's the display name rather than the CLAN# id (normalizeClanId
  // slugifies either form the same way).
  const applyMut = useMutation({
    mutationFn: () => api.applyToClan(clan.name, { gamertag: gamertag.trim() || undefined, message: message.trim() || undefined }),
    onSuccess: () => {
      setApplyOpen(false);
      setError('');
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const hasLeadership = leadership.length > 0;
  const hasTeams = teams.length > 0;
  const hasMembers = members.length > 0;
  const hasCurrentComps = comps.length > 0;
  const hasHistory = history.length > 0;

  return (
    <div className="min-h-screen text-white">

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section>
        <div className="relative aspect-[16/6] w-full overflow-hidden bg-gradient-to-br from-forge-900/40 via-iron-900 to-iron-950 sm:aspect-[21/6]">
          {clan.bannerUrl && (
            <CoverImage src={clan.bannerUrl} alt="" className="h-full w-full object-cover" />
          )}
          {/* Scrim to keep the name/logo readable regardless of banner contents */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-iron-950/90 via-iron-950/20 to-transparent" />
        </div>

        <div className="mx-auto max-w-4xl px-4 pb-8 sm:px-6">
          <div className="-mt-10 flex items-end gap-4 sm:-mt-12">
            <div className="relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-iron-900 ring-2 ring-iron-950 sm:h-24 sm:w-24">
              {clan.logoUrl ? (
                <CoverImage src={clan.logoUrl} alt={`${clan.name} logo`} className="h-full w-full object-cover" />
              ) : (
                <span className="font-display text-2xl font-black uppercase tracking-tight text-forge-400 sm:text-3xl">
                  {monogram(clan.name)}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1 pb-1">
              <h1 className="truncate font-display text-2xl font-black uppercase tracking-tight text-white sm:text-4xl">
                {clan.name}
              </h1>
            </div>
          </div>

          {clan.description && (
            <p className="mt-4 max-w-2xl whitespace-pre-line text-sm leading-relaxed text-iron-400 sm:text-base">
              {clan.description}
            </p>
          )}
        </div>
      </section>

      {/* ── Stats bar (mirrors OrgPublicPageView's ~L171-179) ───────────── */}
      <section className="border-y border-iron-800 bg-surface-950">
        <div className="mx-auto grid max-w-4xl grid-cols-3 gap-6 px-4 py-6 text-center sm:px-6">
          <StatCell label="Members" value={stats.memberCount} />
          <StatCell label="Teams" value={stats.teamCount} />
          <StatCell label="Comps entered" value={stats.compsEntered} />
        </div>
      </section>

      <div className="mx-auto max-w-4xl space-y-12 px-4 py-12 sm:px-6">

        {/* ── Apply to join ─────────────────────────────────────────────── */}
        {clan.applicationsOpen ? (
          <section className="flex flex-col gap-3 rounded-lg bg-emerald-950/30 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <DoorOpen className="h-4 w-4 shrink-0 text-emerald-300" strokeWidth={2.5} />
              <p className="text-sm text-emerald-100">This clan is accepting new members.</p>
            </div>
            <button
              onClick={() => setApplyOpen(true)}
              className="shrink-0 rounded-lg bg-emerald-600 px-4 py-2 font-display text-[11px] font-black uppercase tracking-wider text-white transition-colors hover:bg-emerald-500"
            >
              <Send className="mr-1 inline h-3 w-3" strokeWidth={3} />
              Apply
            </button>
          </section>
        ) : (
          <section className="rounded-lg bg-iron-900 px-4 py-3">
            <p className="text-sm text-iron-500">This clan isn't accepting applications right now.</p>
          </section>
        )}

        {/* ── Leadership & roles ───────────────────────────────────────── */}
        {hasLeadership && (
          <section>
            <SectionLabel icon={Crown}>Leadership</SectionLabel>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {leadership.map((l, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between gap-3 rounded-lg border border-iron-800 bg-iron-950 px-4 py-3"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    {leadershipIcon(l.role)}
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-white">
                        {l.gamertag || l.displayName}
                      </p>
                      {l.gamertag && l.gamertag !== l.displayName && (
                        <p className="truncate text-xs text-iron-500">{l.displayName}</p>
                      )}
                    </div>
                  </div>
                  <span className="shrink-0 font-display text-[10px] font-black uppercase tracking-[0.18em] text-forge-400">
                    {clanRoleDisplayName(l.role as ClanScopedRole) || l.role}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Teams ─────────────────────────────────────────────────────── */}
        {hasTeams && (
          <section>
            <SectionLabel icon={Swords}>Teams</SectionLabel>
            <div className="overflow-x-auto">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {teams.map(team => (
                  <div key={team.teamNo} className="rounded-lg border border-iron-800 bg-iron-950 p-4">
                    <p className="truncate font-display text-sm font-black uppercase tracking-wide text-white">
                      {team.name}
                    </p>
                    <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-iron-500">
                      Team {team.teamNo}
                    </p>
                    {team.members.length === 0 ? (
                      <p className="text-sm text-iron-500">No players assigned.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {team.members.map((m, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-iron-300">
                            <Gamepad2 className="h-3.5 w-3.5 shrink-0 text-iron-500" />
                            <span className="truncate">{m.gamertag || m.displayName}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Members (full roster) ────────────────────────────────────── */}
        {hasMembers && (
          <section>
            <SectionLabel icon={Users}>Roster</SectionLabel>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {members.map((m, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between gap-3 rounded-lg bg-iron-900 px-4 py-3"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    {memberIcon(m.role)}
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-white">
                        {m.gamertag || m.displayName}
                      </p>
                      {m.teamNo != null && (
                        <p className="text-xs text-iron-500">Team {m.teamNo}</p>
                      )}
                    </div>
                  </div>
                  <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.18em] text-iron-500">
                    {clanRoleDisplayName(m.role as ClanScopedRole)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Currently competing in ───────────────────────────────────── */}
        {hasCurrentComps && (
          <section>
            <SectionLabel icon={Swords}>Currently Competing In</SectionLabel>
            <div className="space-y-3">
              {comps.map(c => (
                <div
                  key={c.compId}
                  className="flex items-center justify-between gap-3 rounded-lg border border-iron-800 bg-iron-950 px-4 py-3"
                >
                  <p className="truncate text-sm font-medium text-white">{c.name}</p>
                  <StatusBadge status={c.status} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Comp history ──────────────────────────────────────────────── */}
        {hasHistory && (
          <section>
            <SectionLabel icon={History}>Comp History</SectionLabel>
            <div className="space-y-3">
              {history.map(h => (
                <div
                  key={h.compId}
                  className="flex items-center justify-between gap-3 rounded-lg border border-iron-800 bg-iron-950 px-4 py-3"
                >
                  <p className="truncate text-sm font-medium text-white">{h.name}</p>
                  {h.result ? (
                    <span className="flex shrink-0 items-center gap-1.5 text-sm font-medium text-forge-300">
                      {h.placement != null && (
                        <span>{PLACE_MEDALS[h.placement] ?? `#${h.placement}`}</span>
                      )}
                      {h.result}
                    </span>
                  ) : (
                    <span className="shrink-0 text-xs text-iron-500">No podium finish</span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* ── Apply modal (lifted from ClanDetail.tsx ~L192-235) ──────────── */}
      {applyOpen && (
        <>
          <div
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
            onClick={() => !applyMut.isPending && setApplyOpen(false)}
          />
          <div className="fixed left-1/2 top-1/2 z-[80] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl bg-iron-900 p-5 shadow-elev3">
            <p className="font-display text-[10px] font-black uppercase tracking-[0.22em] text-forge-400">Apply to</p>
            <p className="mt-1 font-display text-xl font-black uppercase tracking-tight text-white">{clan.name}</p>

            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block font-display text-[10px] font-black uppercase tracking-[0.22em] text-iron-400">
                  Your Gamertag
                </label>
                <input value={gamertag} onChange={e => setGamertag(e.target.value)} placeholder="Gamertag#tag" className="input w-full" />
              </div>
              <div>
                <label className="mb-1 block font-display text-[10px] font-black uppercase tracking-[0.22em] text-iron-400">
                  Message (optional)
                </label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} placeholder="Tell the leader why you want in" className="input w-full resize-none" />
              </div>

              {error && <p className="rounded-lg bg-red-950/50 px-3 py-2 text-xs text-red-300">{error}</p>}

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setApplyOpen(false)}
                  disabled={applyMut.isPending}
                  className="flex-1 rounded-lg bg-iron-800 px-3 py-2.5 font-display text-[11px] font-black uppercase tracking-wider text-iron-200 transition-colors hover:bg-iron-700 hover:text-white disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={() => applyMut.mutate()}
                  disabled={applyMut.isPending}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-forge-500 px-3 py-2.5 font-display text-[11px] font-black uppercase tracking-wider text-iron-950 transition-colors hover:bg-forge-400 disabled:opacity-50"
                >
                  {applyMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  Submit
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {applyMut.isSuccess && !applyOpen && (
        <div className="fixed bottom-6 right-6 z-[90] flex items-center gap-2 rounded-lg bg-iron-950 px-4 py-3 shadow-elev2 ring-1 ring-emerald-500/20">
          <CheckCircle2 className="h-4 w-4 text-emerald-300" strokeWidth={2.5} />
          <p className="text-sm text-white">Application sent.</p>
        </div>
      )}

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="border-t border-iron-800 py-8 text-center text-xs text-iron-500">
        Powered by{' '}
        <a href="https://goodgameanvil.com" className="transition-colors hover:text-iron-400">
          GG Anvil
        </a>
      </footer>
    </div>
  );
}
